import React from 'react';

import { createSearchParams } from 'react-router-dom';

import { Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { toInclusiveEndDate } from '~/components/Content/SearchAnalytics/SearchBigQueryDateRangePicker';

import type { AnalyticsDateRange } from '~/services/AnalyticsService';

import SectionedList from './SectionedList';
import type { GroupedSearchStats } from './groupSearchStats';

type GroupedSearchTableProps = {
  groups: Array<GroupedSearchStats>;
  dateRange: AnalyticsDateRange;
};

function buildReproduceSearchHref(searchId: string, dateRange: AnalyticsDateRange) {
  const inclusiveEndDate = toInclusiveEndDate(dateRange.endDate);

  return `/search/reproduce-search?${createSearchParams({
    searchId,
    startDate: dateRange.startDate,
    endDate: inclusiveEndDate,
  }).toString()}`;
}

const COUNT_COLUMN_WIDTH = 80;
// Fixed table layout ignores min()/clamp() widths, so cap the query column at 800px
// via the xl breakpoint (below it, 50% of the page is always under 800px)
const QUERY_COLUMN_WIDTH = { xs: '50%', xl: '800px' };

export default function GroupedSearchTable({ groups, dateRange }: GroupedSearchTableProps) {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': { verticalAlign: 'top', overflowWrap: 'break-word' },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: COUNT_COLUMN_WIDTH }}>Count</TableCell>
            <TableCell sx={{ width: QUERY_COLUMN_WIDTH }}>Natural language query</TableCell>
            <TableCell>Extracted</TableCell>
            <TableCell>Prefix matches</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TableRow key={group.naturalLanguageQuery}>
              <TableCell>{group.count.toLocaleString()}</TableCell>
              <TableCell>
                <Link
                  href={buildReproduceSearchHref(group.firstSearchId, dateRange)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {group.naturalLanguageQuery}
                </Link>
              </TableCell>
              <TableCell>
                <SectionedList
                  sections={[
                    { label: 'Search types', items: group.destinationTypes },
                    { label: 'Destinations', items: group.destinationIds },
                    { label: 'Landmarks', items: group.landmarkIds },
                    { label: 'Properties', items: group.propertyIds },
                  ]}
                />
              </TableCell>
              <TableCell>
                <SectionedList
                  sections={[
                    { label: 'Places', items: group.prefixMatchedGeometryNames },
                    { label: 'Properties', items: group.prefixMatchedOfferNames },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
